import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  type ReactElement,
} from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { loadBundle } from '@/i18n/loadBundle';
import { resolveRouteBundle } from '@/i18n/registry';
import { isGloballyTranslatedKey } from '@/utils/i18n/globalTranslationKeyPrefixes';
import {
  HUB_UI_TRANSLATIONS_QUERY_KEY_ROOT,
  hubUiTranslationsQueryKey,
} from '@/utils/i18n/hubUiTranslationsQueryKey';
import { resolveTranslation } from '@/utils/i18n/resolveTranslation';
import type {
  HubTranslationContextValue,
  HubTranslationProviderProps,
  TranslateHubUiFn,
} from '@/utils/i18n/types';
import useSession from '@/utils/hooks/useSession';
import { resolveSessionUiLanguage } from '@/utils/languages/supportedLanguages';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

const defaultContextValue: HubTranslationContextValue = {
  translateHubUi: (key, fallback, vars) =>
    resolveTranslation(key, fallback, {}, false, 'en', vars),
  locale: 'en',
  routeInScope: false,
  isLoading: false,
  isError: false,
  isReady: true,
};

const HubTranslationContext =
  createContext<HubTranslationContextValue>(defaultContextValue);

export function HubTranslationProvider({
  children,
  initialTranslations,
  initialLocale,
  initialScopeKey,
}: PropsWithChildren<HubTranslationProviderProps>): ReactElement {
  const router = useRouter();
  const session = useSession();
  const locale = resolveSessionUiLanguage(session);
  const { routeInScope, scopeKey } = resolveRouteBundle(router.pathname);
  const shouldFetch = locale !== 'en';
  const hasMatchingInitialData =
    initialTranslations != null &&
    initialLocale === locale &&
    initialScopeKey === scopeKey;

  const { data, isLoading, isError } = useQuery({
    queryKey: hubUiTranslationsQueryKey(locale, scopeKey),
    queryFn: () => loadBundle(locale, router.pathname),
    enabled: shouldFetch,
    initialData: hasMatchingInitialData ? initialTranslations : undefined,
    // Match cacheTime — refetch only when stale (scope/locale/bundle version change).
    staleTime: TWENTY_FOUR_HOURS_MS,
    refetchOnMount: true,
    cacheTime: TWENTY_FOUR_HOURS_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const bundle = useMemo(() => {
    if (data) {
      return data;
    }
    if (hasMatchingInitialData && initialTranslations) {
      return initialTranslations;
    }
    return {};
  }, [data, hasMatchingInitialData, initialTranslations]);

  const translateHubUi = useCallback<TranslateHubUiFn>(
    (key, fallback, vars) => {
      const keyEnabled = routeInScope || isGloballyTranslatedKey(key);
      return resolveTranslation(
        key,
        fallback,
        bundle,
        keyEnabled,
        locale,
        vars
      );
    },
    [bundle, routeInScope, locale]
  );

  const isReady =
    !shouldFetch ||
    (!isError && (hasMatchingInitialData || (!isLoading && data != null)));

  const value = useMemo(
    () => ({
      translateHubUi,
      locale,
      routeInScope,
      isLoading: shouldFetch && isLoading && !hasMatchingInitialData,
      isError: shouldFetch && isError,
      isReady,
    }),
    [
      translateHubUi,
      locale,
      routeInScope,
      shouldFetch,
      isLoading,
      isError,
      isReady,
      hasMatchingInitialData,
    ]
  );

  return (
    <HubTranslationContext.Provider value={value}>
      {children}
    </HubTranslationContext.Provider>
  );
}

export { HUB_UI_TRANSLATIONS_QUERY_KEY_ROOT };

export function useHubTranslationContext(): HubTranslationContextValue {
  return useContext(HubTranslationContext);
}
